/**
 * The dashboard's "how I see data" preferences — one reactive blob, server-backed.
 *
 * Holds the three knobs the Settings page edits (Transport stays OUT — see below):
 *   - `writeScope`   — per-resource default VISIBILITY for a NEW overlay/attach.
 *   - `resolveScope` — per-resource default tenancy tier a RESOLVED view opens at.
 *   - `txFeed`       — the Transactions explorer's default feed scope.
 *
 * Source of truth is a single `saved-views-api` row: the reserved
 * `__prefs:dashboard` surface, `user` scope, blob under `view_state.extra`. That
 * makes a user's choices follow them across devices, and lets a workspace admin
 * (optionally, later) drop a `tenant`-scope row as the shared STARTING POINT —
 * pick semantics, not a tenancy-ladder merge: my own row wins, else the tenant
 * row, else the product defaults in `@/config`.
 *
 * `ScopedStorage` stays the SYNCHRONOUS local cache: it seeds the first paint
 * (and keeps working offline / pre-auth), and every change writes through BOTH
 * the cache and the server. The server write is best-effort — the UI always
 * reads the local state/cache, so a failed/absent network round-trip never
 * blocks a setting from taking effect.
 *
 * `useWriteScopePrefs` / `useReadScopePrefs` are thin facades over this store, so
 * their existing call sites (catalog adapter, graph assignment, resolve hooks,
 * Settings, the explorer) are unchanged. Transport (REST vs GraphQL) is
 * deliberately excluded: it decides WHICH API a read uses, so it can't itself
 * depend on a network read — it lives on `FeatureFlags` / localStorage.
 */
import * as React from "react";
import { useApiClient } from "@authe/ui-core/api";
import { useOptionalTenant } from "@authe/ui-core/tenant";
import { createScopedStorage } from "@authe/fe-commons/storage";
import type { AddressScope } from "@authe/fe-commons/graphql";
import { createApiSavedViewsSource, emptyView, type SavedView } from "@authe/saved-views";
import { APPLICATION_SLUG, type OverlayResource, type OverlayWriteScope } from "@/config";
import type { TxScopeId } from "@/features/transactions/scopes";
import {
  CACHE_KEY,
  EMPTY_BLOB,
  PREFS_TABLE_ID,
  PREFS_VIEW_NAME,
  isKnownTxScope,
  normalizeBlob,
  type DashboardPrefsBlob,
} from "./dashboardPrefs.blob";

export type {
  DashboardPrefsBlob,
  WriteScopeMap,
  ResolveScopeMap,
} from "./dashboardPrefs.blob";
export { normalizeBlob } from "./dashboardPrefs.blob";

export interface DashboardPrefs {
  /** The live, resolved-onto-defaults-by-callers blob (never null). */
  blob: DashboardPrefsBlob;
  /** Set (or clear with `null`) a per-resource default write scope. */
  setWriteScope: (resource: OverlayResource, scope: OverlayWriteScope | null) => void;
  /** Set (or clear with `null`) a per-resource default resolve tier. */
  setResolveScope: (resource: OverlayResource, scope: AddressScope | null) => void;
  /** Set (or clear with `null`) the Transactions feed default. */
  setTxFeed: (scope: TxScopeId | null) => void;
}

/** Provider-less fallback: in-memory empties, no persistence (tests / isolated renders). */
const FALLBACK: DashboardPrefs = {
  blob: EMPTY_BLOB,
  setWriteScope: () => {},
  setResolveScope: () => {},
  setTxFeed: () => {},
};

const DashboardPrefsContext = React.createContext<DashboardPrefs | null>(null);

export function DashboardPrefsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const tenant = useOptionalTenant();
  const client = useApiClient();

  const customerId = tenant?.customerId ?? null;
  const orgId = tenant?.orgId ?? null;
  const tenantId = tenant?.tenantId ?? null;
  const userId = tenant?.userId ?? null;

  // Identity-scoped keyspace: fresh on a tenant/user switch so a cached blob
  // never bleeds between identities (same pattern as brand overrides).
  const storage = React.useMemo(
    () => createScopedStorage({ app: APPLICATION_SLUG, customerId, orgId, tenantId, userId }),
    [customerId, orgId, tenantId, userId],
  );
  const source = React.useMemo(() => createApiSavedViewsSource(client), [client]);

  const [blob, setBlob] = React.useState<DashboardPrefsBlob>(() =>
    normalizeBlob(storage.get<DashboardPrefsBlob>(CACHE_KEY, EMPTY_BLOB)),
  );

  // Mirror the latest blob for the setters (compose consecutive edits without a
  // side effect inside a state updater, which StrictMode double-invokes).
  const blobRef = React.useRef(blob);
  // The server row id for THIS identity's `user` view; null until created/hydrated.
  const userViewId = React.useRef<string | null>(null);
  // Serialise server writes so a burst can't race a create into duplicate rows.
  const writeChain = React.useRef<Promise<void>>(Promise.resolve());

  const setBoth = React.useCallback(
    (next: DashboardPrefsBlob) => {
      blobRef.current = next;
      setBlob(next);
    },
    [],
  );

  // Seed from the local cache whenever the identity (keyspace) changes.
  React.useEffect(() => {
    userViewId.current = null;
    setBoth(normalizeBlob(storage.get<DashboardPrefsBlob>(CACHE_KEY, EMPTY_BLOB)));
  }, [storage, setBoth]);

  // Hydrate from the server once per authenticated identity. Pick semantics:
  // the caller's own `user` row wins; else the `tenant` starting point; else we
  // keep whatever the cache/defaults already gave us. Best-effort.
  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      try {
        const views = await source.list({ tableId: PREFS_TABLE_ID });
        if (cancelled) return;
        const mine = views.find((v) => v.scope === "user");
        if (mine) userViewId.current = mine.id;
        const chosen = mine ?? views.find((v) => v.scope === "tenant");
        if (!chosen) return;
        const next = normalizeBlob(chosen.state?.extra);
        setBoth(next);
        storage.set(CACHE_KEY, next);
      } catch {
        // Offline / not provisioned yet — the local cache + defaults stand in.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, storage, userId, setBoth]);

  // Write-through: cache immediately (drives UX + next first paint), then push
  // the caller's `user` row to the server on a serialised, best-effort chain.
  const persist = React.useCallback(
    (next: DashboardPrefsBlob) => {
      storage.set(CACHE_KEY, next);
      if (!userId) return;
      const state: SavedView["state"] = { extra: next as unknown as Record<string, unknown> };
      writeChain.current = writeChain.current.then(async () => {
        try {
          if (userViewId.current) {
            await source.update({
              id: userViewId.current,
              name: PREFS_VIEW_NAME,
              tableId: PREFS_TABLE_ID,
              scope: "user",
              state,
              createdAt: 0,
              updatedAt: 0,
            });
          } else {
            const created = await source.create({
              ...emptyView(PREFS_TABLE_ID, "user"),
              name: PREFS_VIEW_NAME,
              state,
            });
            userViewId.current = created.id;
          }
        } catch {
          // Best-effort: the local cache already reflects the change.
        }
      });
    },
    [source, storage, userId],
  );

  const apply = React.useCallback(
    (next: DashboardPrefsBlob) => {
      setBoth(next);
      persist(next);
    },
    [setBoth, persist],
  );

  const setWriteScope = React.useCallback(
    (resource: OverlayResource, scope: OverlayWriteScope | null) => {
      const writeScope = { ...blobRef.current.writeScope };
      if (scope == null) delete writeScope[resource];
      else writeScope[resource] = scope;
      apply({ ...blobRef.current, writeScope });
    },
    [apply],
  );

  const setResolveScope = React.useCallback(
    (resource: OverlayResource, scope: AddressScope | null) => {
      const resolveScope = { ...blobRef.current.resolveScope };
      if (scope == null) delete resolveScope[resource];
      else resolveScope[resource] = scope;
      apply({ ...blobRef.current, resolveScope });
    },
    [apply],
  );

  const setTxFeed = React.useCallback(
    (scope: TxScopeId | null) => {
      apply({ ...blobRef.current, txFeed: isKnownTxScope(scope) ? scope : null });
    },
    [apply],
  );

  const value = React.useMemo<DashboardPrefs>(
    () => ({ blob, setWriteScope, setResolveScope, setTxFeed }),
    [blob, setWriteScope, setResolveScope, setTxFeed],
  );

  return <DashboardPrefsContext.Provider value={value}>{children}</DashboardPrefsContext.Provider>;
}

/** Access the raw dashboard prefs store (prefer the `useWriteScopePrefs` / `useReadScopePrefs` facades). */
export function useDashboardPrefs(): DashboardPrefs {
  return React.useContext(DashboardPrefsContext) ?? FALLBACK;
}
